import {
  deleteOrderTemplateService,
  fetchOrderTemplateCall,
  fetchOrderTemplates,
  saveOrderTemplateCall,
} from '@store/orders/services/orderTemplatesServices';
import {
  orderStore,
  setOrderLoading,
  setOrderStore,
} from '@store/orders/store';
import { userStore } from '@store/user/store';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { isDateLessThanCurrentDate } from '@utils/dateUtils';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { cloneBreakdownsForInsert } from '@utils/ltl/loadItemBreakdown';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import { DateTime } from 'luxon';
import {
  mapStopFacilityDetails,
  serializeFacilityDetails,
} from '@common/facilityDetails';

import { fetchPostLoadByPostingId } from './postLoadActions';
import { getCarrierDetail, getCarrierFeatures } from './carrierActions';
import { loadCustomerInfo } from './orderCustomerActions';

export const getOrderTemplates = async () => {
  const response = await fetchOrderTemplates();
  const templates = Array.isArray(response) ? response : [];
  setOrderStore('orderTemplates', templates);
};

export const deleteOrderTemplate = async (
  id: number,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  const response = await deleteOrderTemplateService(id, changeTrackingSource);
  if (Boolean(response)) {
    const newTemplates = orderStore.orderTemplates.filter(
      (template) => template.id !== id,
    );
    setOrderStore('orderTemplates', newTemplates);
  }
};

export const saveOrderTemplate = async (
  name: string,
  isPublic: boolean,
  cb?: () => void,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  let template: Partial<IOrderViewModel> = cloneDeep(orderStore.order);
  template = omit(template, ['id']);
  template.payments = [];
  template.loads = template.loads?.map((load) => {
    if (load.mode === 'TL' || load.mode === 'Partial') {
      load.stops = load.stops?.map((stop) => ({
        ...stop,
        driverInTime: undefined,
        driverOutTime: undefined,
      }));
    }
    return load;
  });
  await saveOrderTemplateCall(
    name,
    isPublic,
    mapStopFacilityDetails(template, serializeFacilityDetails),
    0,
    cb,
    changeTrackingSource,
  );
};

export const fetchOrderTemplateData = async (
  templateId: number,
  callback?: () => void,
) => {
  try {
    setOrderLoading(true);
    const order = await fetchOrderTemplateCall(templateId);
    if (!Boolean(order)) return;
    order.id = 0;
    order.customerId !== null && (await loadCustomerInfo(order.customerId));
    if (order.loads[0].carrierId != null) {
      await getCarrierFeatures(order.loads[0].carrierId, 0);
      await getCarrierDetail(order.loads[0].carrierId, 0);
    }
    if (order.loads[0].postingId !== null)
      await fetchPostLoadByPostingId(order.loads[0].postingId);
    let stopId = -1;
    const updatedLoads = order.loads.map((load, idx) => {
      load.coveredBy = userStore.user.name;
      load.coveredById = userStore.user.id;
      load.id = -(idx + 1);
      load.useStatusStateMachine = true;
      load.stops = load.stops?.map((stop) => {
        stop.id = stopId--;
        stop.loadId = load.id;
        stop = omit(stop, 'load');
        if (stop.stopDateTime !== null && stop.stopDateTime !== undefined) {
          if (isDateLessThanCurrentDate(DateTime.fromISO(stop.stopDateTime))) {
            stop.stopDateTime = undefined;
          }
        } else {
          stop.stopDateTime = undefined;
        }
        if (load.mode === 'TL' || load.mode === 'Partial') {
          stop.driverInTime = undefined;
          stop.driverOutTime = undefined;
        }
        return stop;
      });
      load.items = load.items?.map((item) => {
        item.id = 0;
        if (item.loadItemBreakdowns) {
          item.loadItemBreakdowns = cloneBreakdownsForInsert(
            item.loadItemBreakdowns,
          );
        }
        if (load.stops) {
          const firstPickUp = load.stops.find((stop) => stop.pickUp);
          const firstDropOff = load.stops.find((stop) => stop.dropOff);
          item.pickUpId = (firstPickUp && firstPickUp.id) || 0;
          item.dropOffId = (firstDropOff && firstDropOff.id) || 0;
        }
        return item;
      });
      return load;
    });
    order.metadata = []; //setting metadata to empty array as it asks for orderId which is not present in template
    setOrderStore(() => ({
      order: {
        ...order,
        loads: updatedLoads,
      },
    }));
  } catch (error) {
    logError('fetchOrderTemplateData', error as Error);
    setOrderStore(() => ({
      isError: true,
      isCreate: false,
      isTemplateUsedInOrder: false,
    }));
  } finally {
    setOrderStore('loading', false);
    callback && callback();
  }
};
